import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Cookies from 'js-cookie';
import { setStatusFail } from '../statusFail/statusFailSlice';
// Define the initial state
const initialState = {
  rosterLocations: [],
  locationDoctors: [],
  rostersData: [],
  rostersRequestId: null,
  doctorsRequestId: null,
  loading: false,  
  error: null,
};

const ADMIN_URL =  process.env.REACT_APP_ADMIN_URL
const ACCOUNTS_URL = process.env.REACT_APP_ACCOUNTS_URL
 

export const fetchLocationsForRosters = createAsyncThunk(
  'locations/fetchLocationsForRosters',
  async (_, { rejectWithValue,dispatch }) => {
    try {
      const response = await axios.get(`${ADMIN_URL}/locations`, {
        headers: {
          Authorization: `Bearer ${Cookies.get("Token")}`,
          "tenantId":Cookies.get("TenantId")
        },
      });

      return response.data.data.data;
    } catch (error) {
       if(error.response?.data?.errorCode == "STATUS_CHECK_TENANT_DENIED"){
              const route = "/status-failed"
              await dispatch(setStatusFail({tenants:error.response.data.tenants,navigate:route}))
            }
      return rejectWithValue(error.response.data?.error?.message || "Something went wrong");
    }
  }
);

 
export const profilesOfLocation = createAsyncThunk(
  'locations/profilesOfLocation',
  async (locationId, { rejectWithValue,dispatch }) => {
    try {
      const response = await axios.get(`${ACCOUNTS_URL}/profiles/location-profile`, {
        headers: {
          Authorization: `Bearer ${Cookies.get("Token")}`,
          "tenantid":Cookies.get("TenantId"),
          locationId,
          usertype: "practitioner",
        },
      });
 
      return response.data.data;
    } catch (error) {
      if(error.response?.data?.errorCode == "STATUS_CHECK_TENANT_DENIED"){
        const route = "/status-failed"
        await dispatch(setStatusFail({tenants:error.response.data.tenants,navigate:route}))
      }
      return rejectWithValue(error.response?.data?.error?.message || "Something went wrong");
    }
  }
);

 
export const saveRoster = createAsyncThunk(
  'rosters/saveRoster',
  async (rosterData, { rejectWithValue,dispatch }) => {
    try {
      const response = await axios.post(`${ADMIN_URL}/rosters`, rosterData, {
        headers: {
          Authorization: `Bearer ${Cookies.get("Token")}`,
          "tenantId":Cookies.get("TenantId")
        },
      });
      
      return response.data.data;
    } catch (error) {
      if(error.response?.data?.errorCode == "STATUS_CHECK_TENANT_DENIED"){
        const route = "/status-failed"
        await dispatch(setStatusFail({tenants:error.response.data.tenants,navigate:route}))
      }
      return rejectWithValue(error.response?.data?.error?.message || "Something went wrong");
    }
  }
);


 
export const getRosters = createAsyncThunk(
  'rosters/getRosters',
  async (userIds, { rejectWithValue,dispatch }) => {
    try {
      const rosters = await Promise.all(userIds.map((userId) =>
        axios.get(`${ADMIN_URL}/rosters/practitioner-roster/${userId}`, {
          headers: {
            Authorization: `Bearer ${Cookies.get("Token")}`,
            tenantid:Cookies.get("TenantId")
          },
        }).then((response) => response.data.data).catch((error) => {
          // The API answers 404 when the practitioner simply has no roster yet; that is an empty calendar, not an error.
          if (error.response?.status === 404) return [];
          throw error;
        })
      ));

      return rosters.flat();
    } catch (error) {
      if(error.response?.data?.errorCode == "STATUS_CHECK_TENANT_DENIED"){
        const route = "/status-failed"
        await dispatch(setStatusFail({tenants:error.response.data.tenants,navigate:route}))
      }
      return rejectWithValue(error.response.data?.error?.message || "Something went wrong");
    }
  }
);

// Update the slice to include the new thunk
const rosterSlice = createSlice({
  name: 'rosters',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRosters: (state) => {
      state.rostersData = [];
      state.rostersRequestId = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLocationsForRosters.pending, (state) => {
        state.error = null; 
      })
      .addCase(fetchLocationsForRosters.fulfilled, (state, action) => {
        state.rosterLocations = action.payload;
      })
      .addCase(fetchLocationsForRosters.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(profilesOfLocation.pending, (state, action) => {
        state.error = null;  
        state.locationDoctors = [];
        state.doctorsRequestId = action.meta.requestId;
      })
      .addCase(profilesOfLocation.fulfilled, (state, action) => {
        if (state.doctorsRequestId !== action.meta.requestId) return;
        state.locationDoctors = action.payload;
      })
      .addCase(profilesOfLocation.rejected, (state, action) => {
        if (state.doctorsRequestId !== action.meta.requestId) return;
        state.error = action.payload;
      })
      .addCase(saveRoster.pending, (state) => {
        state.loading = true; 
        state.error = null;  
      })
      .addCase(saveRoster.fulfilled, (state, action) => {
        state.loading = false; 
        state.rostersData.push(action.payload);
      })
      // The page reports failed days itself, so setting error here would toast them twice.
      .addCase(saveRoster.rejected, (state) => {
        state.loading = false; 
      })
      // Drop the previous doctor's rosters straight away, and ignore any response but the latest.
      .addCase(getRosters.pending, (state, action) => {
        state.loading = true;  
        state.error = null;  
        state.rostersData = [];
        state.rostersRequestId = action.meta.requestId;
      })
      .addCase(getRosters.fulfilled, (state, action) => {
        if (state.rostersRequestId !== action.meta.requestId) return;
        state.loading = false; 
        state.rostersData = action.payload;
      })
      .addCase(getRosters.rejected, (state, action) => {
        if (state.rostersRequestId !== action.meta.requestId) return;
        state.loading = false;  
        state.error = action.payload;
      });
  },
});

export const { clearError, clearRosters } = rosterSlice.actions;
// Export the reducer
export default rosterSlice.reducer;
